/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// แถบตัวกรองของหน้า "ดาวน์โหลดรูป" (1 พ.ย. 2569 · เจ้าของงาน: สินค้าเยอะ ต้องกรองตาม หมวด/สาขา/รูป AI ได้)
//   สินค้า: ค้นหา · แผนก → หมวด → กลุ่ม (เลือกแม่แล้วลูกกรองตาม · เปลี่ยนแม่ = ล้างลูก) · ยี่ห้อ (autocomplete 1,698 ตัว)
//           · สาขาที่ขาย (= มีราคาสาขาใน products_price) · รูป (มี/ไม่มี/มี AI) · รูปอัปเดตใน · ขายออนไลน์
//   แคมเปญ: ค้นหา · สถานะ (วัน+เวลาแบบเครื่องคิดเงิน) · สาขา · รูป · เริ่มตั้งแต่/สิ้นสุดภายใน
//   ชุดกรองด่วน = ตั้งหลายช่องทีเดียว (กดซ้ำ = เอาออก) · ชิปตัวกรองที่ใช้อยู่กด × ทีละตัวได้
//   ส่งค่าให้ backend ตามชื่อใน controllers/image_export.con.php (dep_id/cat_id/grp_id/sub_id = ความหมายจริง ไม่ใช่ชื่อคอลัมน์)
import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import MDBox from 'components/MDBox';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

export const PRODUCT_DEFAULT = {
  search: '',
  dep_id: 0,
  cat_id: 0,
  grp_id: 0,
  sub_id: 0,
  brand: null, // {id, name}
  branch_id: 0,
  image: '',
  updated_days: 0,
  new_days: 0,
  online: false,
};
export const CAMPAIGN_DEFAULT = {
  search: '',
  status: '',
  branch_id: 0,
  image: '',
  date_from: '',
  date_to: '',
};

/* ชุดกรองด่วน — patch ทับค่าปัจจุบัน · active เมื่อทุกคีย์ใน patch ตรงกับค่าปัจจุบัน */
export const PRODUCT_PRESETS = [
  { key: 'ai', label: 'มีรูป AI อนุมัติแล้ว', icon: 'auto_awesome', patch: { image: 'ai' } },
  { key: 'upd30', label: 'รูปอัปเดต 30 วัน', patch: { updated_days: 30, image: 'with' } },
  { key: 'online', label: 'ขายออนไลน์ + มีรูป', patch: { online: true, image: 'with' } },
  { key: 'new90', label: 'สินค้าใหม่ 90 วัน', patch: { new_days: 90 } },
  { key: 'noimg', label: 'ยังไม่มีรูป', patch: { image: 'without' } },
];
export const CAMPAIGN_PRESETS = [
  {
    key: 'activeai',
    label: 'ใช้อยู่ + มีกราฟิก AI',
    icon: 'auto_awesome',
    patch: { status: 'active', image: 'ai' },
  },
  { key: 'soon', label: 'ใกล้หมดอายุ 7 วัน', patch: { status: 'soon' } },
  { key: 'noimg', label: 'ยังไม่มีกราฟิก', patch: { status: 'active', image: 'without' } },
];
/* "สาขาของฉัน" ต้องรู้ branch ของผู้ใช้ก่อน — สร้างตอน render */
export const myBranchPreset = (branchId) => ({
  key: 'mybranch',
  label: 'สาขาของฉัน',
  patch: { branch_id: branchId },
});

export const presetActive = (preset, value) =>
  Object.keys(preset.patch).every((k) => String(value[k]) === String(preset.patch[k]));

export const IMAGE_LABEL = { '': 'ทุกสินค้า', with: 'มีรูป', without: 'ไม่มีรูป', ai: 'มีรูป AI' };
export const CAMPAIGN_IMAGE_LABEL = {
  '': 'ทุกแคมเปญ',
  with: 'มีรูปแคมเปญ',
  without: 'ยังไม่มีรูป',
  ai: 'มีกราฟิก AI',
};
export const STATUS_LABEL = {
  '': 'ทุกสถานะ',
  active: 'ใช้อยู่',
  soon: 'ใกล้หมดอายุ (7 วัน)',
  upcoming: 'ยังไม่เริ่ม',
  expired: 'หมดอายุแล้ว',
};
const DAYS_LABEL = { 0: 'ทุกช่วงเวลา', 7: '7 วันล่าสุด', 30: '30 วันล่าสุด', 90: '90 วันล่าสุด' };

const nameOf = (list, id) => (list || []).find((x) => x.id === Number(id))?.name || `#${id}`;

/* ชิปตัวกรองที่ใช้อยู่ — [{key, label}] · กด × = คืนค่าตั้งต้นของคีย์นั้น (ลูกของหมวดถูกล้างตามใน onRemove) */
export const productChips = (v, meta) => {
  const out = [];
  if (v.dep_id)
    out.push({
      key: 'dep_id',
      label: `แผนก: ${v.dep_id === -1 ? 'ไม่ระบุหมวด' : nameOf(meta.departments, v.dep_id)}`,
    });
  if (v.cat_id) out.push({ key: 'cat_id', label: `หมวด: ${nameOf(meta.categories, v.cat_id)}` });
  if (v.grp_id) out.push({ key: 'grp_id', label: `กลุ่ม: ${nameOf(meta.groups, v.grp_id)}` });
  if (v.sub_id)
    out.push({ key: 'sub_id', label: `กลุ่มย่อย: ${nameOf(meta.subgroups, v.sub_id)}` });
  if (v.brand) out.push({ key: 'brand', label: `ยี่ห้อ: ${v.brand.name}` });
  if (v.branch_id)
    out.push({ key: 'branch_id', label: `สาขา: ${nameOf(meta.branches, v.branch_id)}` });
  if (v.image) out.push({ key: 'image', label: `รูป: ${IMAGE_LABEL[v.image]}` });
  if (v.updated_days) out.push({ key: 'updated_days', label: `รูปอัปเดต ${v.updated_days} วัน` });
  if (v.new_days) out.push({ key: 'new_days', label: `สินค้าใหม่ ${v.new_days} วัน` });
  if (v.online) out.push({ key: 'online', label: 'ขายออนไลน์' });
  return out;
};
export const campaignChips = (v, meta) => {
  const out = [];
  if (v.status) out.push({ key: 'status', label: `สถานะ: ${STATUS_LABEL[v.status]}` });
  if (v.branch_id)
    out.push({ key: 'branch_id', label: `สาขา: ${nameOf(meta.branches, v.branch_id)}` });
  if (v.image) out.push({ key: 'image', label: `รูป: ${CAMPAIGN_IMAGE_LABEL[v.image]}` });
  if (v.date_from) out.push({ key: 'date_from', label: `เริ่มตั้งแต่ ${v.date_from}` });
  if (v.date_to) out.push({ key: 'date_to', label: `สิ้นสุดภายใน ${v.date_to}` });
  return out;
};

/* ค่าที่ส่ง API */
export const productBody = (v) => ({
  search: v.search,
  dep_id: v.dep_id,
  cat_id: v.cat_id,
  grp_id: v.grp_id,
  sub_id: v.sub_id,
  brand_id: v.brand ? v.brand.id : 0,
  branch_id: v.branch_id,
  image: v.image,
  updated_days: v.updated_days,
  new_days: v.new_days,
  online: v.online ? 'Y' : 'N',
});
export const campaignBody = (v) => ({
  search: v.search,
  status: v.status,
  branch_id: v.branch_id,
  image: v.image,
  date_from: v.date_from,
  date_to: v.date_to,
});

const VALUE_SHAPE = PropTypes.shape({
  search: PropTypes.string,
  dep_id: PropTypes.number,
  cat_id: PropTypes.number,
  grp_id: PropTypes.number,
  sub_id: PropTypes.number,
  brand: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  branch_id: PropTypes.number,
  image: PropTypes.string,
  updated_days: PropTypes.number,
  new_days: PropTypes.number,
  online: PropTypes.bool,
  status: PropTypes.string,
  date_from: PropTypes.string,
  date_to: PropTypes.string,
});

const selSx = (active) => ({
  flex: '1 1 150px',
  minWidth: 130,
  maxWidth: 320, // ช่องที่ตกบรรทัดคนเดียวไม่ยืดเต็มแถว
  '& .MuiOutlinedInput-notchedOutline': active ? { borderColor: 'info.main' } : {},
});

function Sel({ label, value, onChange, children, active, flex, ariaLabel }) {
  return (
    <MDInput
      select
      size='small'
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true, 'aria-label': ariaLabel || label }}
      sx={{ ...selSx(active), ...(flex ? { flex } : {}) }}
    >
      {children}
    </MDInput>
  );
}
Sel.defaultProps = { active: false, flex: '', ariaLabel: '' };
Sel.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  active: PropTypes.bool,
  flex: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/**
 * props
 *   tab      0 สินค้า · 1 แคมเปญ
 *   meta     ผลจาก OPTIONS /image_export/filters
 *   value    ค่าตัวกรองปัจจุบัน (PRODUCT_DEFAULT / CAMPAIGN_DEFAULT)
 *   onChange(next, {debounce}) — debounce = true เมื่อเป็นช่องพิมพ์ (หน้าหลักหน่วง 400ms)
 */
function ExportFilters({ tab, meta, value, onChange }) {
  const set = (patch, debounce = false) => onChange({ ...value, ...patch }, { debounce });

  if (tab === 1) {
    return (
      <MDBox display='flex' flexWrap='wrap' gap={1.25} alignItems='center'>
        <MDInput
          size='small'
          label='ค้นหา'
          placeholder='รหัสหรือชื่อแคมเปญ'
          value={value.search}
          onChange={(e) => set({ search: e.target.value }, true)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'ค้นหาแคมเปญ', type: 'search' }}
          sx={{ flex: '2 1 240px', minWidth: 200 }}
        />
        <Sel
          label='สถานะ'
          value={value.status}
          onChange={(s) => set({ status: s })}
          active={Boolean(value.status)}
        >
          {Object.keys(STATUS_LABEL).map((k) => (
            <MenuItem key={k || 'all'} value={k}>
              {STATUS_LABEL[k]}
            </MenuItem>
          ))}
        </Sel>
        <Sel
          label='สาขา'
          value={value.branch_id}
          onChange={(b) => set({ branch_id: Number(b) })}
          active={Boolean(value.branch_id)}
          flex='1 1 190px'
        >
          <MenuItem value={0}>ทุกสาขา</MenuItem>
          {(meta.branches || []).map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </Sel>
        <Sel
          label='รูป'
          value={value.image}
          onChange={(s) => set({ image: s })}
          active={Boolean(value.image)}
        >
          {Object.keys(CAMPAIGN_IMAGE_LABEL).map((k) => (
            <MenuItem key={k || 'all'} value={k}>
              {CAMPAIGN_IMAGE_LABEL[k]}
            </MenuItem>
          ))}
        </Sel>
        <MDInput
          size='small'
          type='date'
          label='เริ่มตั้งแต่'
          value={value.date_from}
          onChange={(e) => set({ date_from: e.target.value }, true)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'เริ่มตั้งแต่วันที่' }}
          sx={selSx(Boolean(value.date_from))}
        />
        <MDInput
          size='small'
          type='date'
          label='สิ้นสุดภายใน'
          value={value.date_to}
          onChange={(e) => set({ date_to: e.target.value }, true)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'สิ้นสุดภายในวันที่' }}
          sx={selSx(Boolean(value.date_to))}
        />
      </MDBox>
    );
  }

  const cats = (meta.categories || []).filter(
    (c) => !value.dep_id || value.dep_id === -1 || c.dep_id === value.dep_id
  );
  const grps = (meta.groups || []).filter((g) => !value.cat_id || g.cat_id === value.cat_id);
  const subs = (meta.subgroups || []).filter((s) => !value.grp_id || s.group_id === value.grp_id);
  return (
    <MDBox display='flex' flexWrap='wrap' gap={1.25} alignItems='center'>
      <MDInput
        size='small'
        label='ค้นหา'
        placeholder='รหัส ชื่อ หรือบาร์โค้ด'
        value={value.search}
        onChange={(e) => set({ search: e.target.value }, true)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ 'aria-label': 'ค้นหาสินค้า', type: 'search' }}
        sx={{ flex: '2 1 240px', minWidth: 200 }}
      />
      <Sel
        label='แผนก'
        value={value.dep_id}
        onChange={(d) => set({ dep_id: Number(d), cat_id: 0, grp_id: 0, sub_id: 0 })}
        active={Boolean(value.dep_id)}
      >
        <MenuItem value={0}>ทุกแผนก</MenuItem>
        {(meta.departments || []).map((d) => (
          <MenuItem key={d.id} value={d.id}>
            {d.name}
          </MenuItem>
        ))}
        <MenuItem value={-1}>ไม่ระบุหมวด</MenuItem>
      </Sel>
      {value.dep_id !== -1 && (
        <Sel
          label='หมวด'
          value={value.cat_id}
          onChange={(c) => set({ cat_id: Number(c), grp_id: 0, sub_id: 0 })}
          active={Boolean(value.cat_id)}
          flex='1 1 180px'
        >
          <MenuItem value={0}>ทุกหมวด</MenuItem>
          {cats.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </Sel>
      )}
      {value.cat_id > 0 && (
        <Sel
          label='กลุ่ม'
          value={value.grp_id}
          onChange={(g) => set({ grp_id: Number(g), sub_id: 0 })}
          active={Boolean(value.grp_id)}
        >
          <MenuItem value={0}>ทุกกลุ่ม</MenuItem>
          {grps.map((g) => (
            <MenuItem key={g.id} value={g.id}>
              {g.name}
            </MenuItem>
          ))}
        </Sel>
      )}
      {value.grp_id > 0 && subs.length > 0 && (
        <Sel
          label='กลุ่มย่อย'
          value={value.sub_id}
          onChange={(s) => set({ sub_id: Number(s) })}
          active={Boolean(value.sub_id)}
        >
          <MenuItem value={0}>ทุกกลุ่มย่อย</MenuItem>
          {subs.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Sel>
      )}
      <Autocomplete
        size='small'
        options={meta.brands || []}
        value={value.brand}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(e, v) => set({ brand: v })}
        noOptionsText='ไม่พบยี่ห้อ'
        sx={{ flex: '1 1 180px', minWidth: 160 }}
        renderInput={(params) => (
          <MDInput
            {...params}
            label='ยี่ห้อ'
            placeholder='พิมพ์ค้นยี่ห้อ'
            InputLabelProps={{ shrink: true }}
            sx={selSx(Boolean(value.brand))}
          />
        )}
      />
      <Sel
        label='สาขาที่ขาย'
        value={value.branch_id}
        onChange={(b) => set({ branch_id: Number(b) })}
        active={Boolean(value.branch_id)}
        flex='1 1 190px'
      >
        <MenuItem value={0}>ทุกสาขา</MenuItem>
        {(meta.branches || []).map((b) => (
          <MenuItem key={b.id} value={b.id}>
            {b.name}
          </MenuItem>
        ))}
      </Sel>
      <Sel
        label='รูป'
        value={value.image}
        onChange={(s) => set({ image: s })}
        active={Boolean(value.image)}
      >
        {Object.keys(IMAGE_LABEL).map((k) => (
          <MenuItem key={k || 'all'} value={k}>
            {IMAGE_LABEL[k]}
          </MenuItem>
        ))}
      </Sel>
      <Sel
        label='รูปอัปเดตใน'
        value={value.updated_days}
        onChange={(d) => set({ updated_days: Number(d) })}
        active={Boolean(value.updated_days)}
      >
        {Object.keys(DAYS_LABEL).map((k) => (
          <MenuItem key={k} value={Number(k)}>
            {DAYS_LABEL[k]}
          </MenuItem>
        ))}
      </Sel>
      <FormControlLabel
        sx={{ m: 0, flex: '0 0 auto' }}
        control={
          <Switch
            size='small'
            checked={value.online}
            onChange={(e) => set({ online: e.target.checked })}
            inputProps={{ 'aria-label': 'เฉพาะที่ขายออนไลน์' }}
          />
        }
        label={<MDTypography variant='button'>ขายออนไลน์</MDTypography>}
      />
    </MDBox>
  );
}
ExportFilters.propTypes = {
  tab: PropTypes.number.isRequired,
  meta: PropTypes.shape({
    departments: PropTypes.arrayOf(PropTypes.object),
    categories: PropTypes.arrayOf(PropTypes.object),
    groups: PropTypes.arrayOf(PropTypes.object),
    subgroups: PropTypes.arrayOf(PropTypes.object),
    brands: PropTypes.arrayOf(PropTypes.object),
    branches: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  value: VALUE_SHAPE.isRequired,
  onChange: PropTypes.func.isRequired,
};

/** แถวชุดกรองด่วน + ชิปที่ใช้อยู่ (ใช้ทั้ง 2 แท็บ) */
export function FilterChips({ presets, value, onPreset, chips, onRemove, onClear }) {
  return (
    <>
      <MDBox display='flex' flexWrap='wrap' alignItems='center' gap={0.75}>
        <MDTypography variant='caption' fontWeight='bold' color='text' sx={{ mr: 0.5 }}>
          ชุดกรองด่วน
        </MDTypography>
        {presets.map((p) => {
          const on = presetActive(p, value);
          return (
            <Chip
              key={p.key}
              size='small'
              clickable
              color={on ? 'info' : 'default'}
              variant={on ? 'filled' : 'outlined'}
              icon={p.icon ? <Icon fontSize='small'>{p.icon}</Icon> : undefined}
              label={p.label}
              onClick={() => onPreset(p, on)}
              aria-pressed={on}
            />
          );
        })}
      </MDBox>
      {chips.length > 0 && (
        <MDBox display='flex' flexWrap='wrap' alignItems='center' gap={0.75} mt={1}>
          {chips.map((c) => (
            <Chip
              key={c.key}
              size='small'
              color='info'
              variant='outlined'
              label={c.label}
              onDelete={() => onRemove(c.key)}
              deleteIcon={<Icon aria-label={`เอาตัวกรอง ${c.label} ออก`}>close</Icon>}
            />
          ))}
          <MDButton size='small' variant='text' color='error' onClick={onClear} sx={{ py: 0 }}>
            ล้างตัวกรอง
          </MDButton>
        </MDBox>
      )}
    </>
  );
}
FilterChips.propTypes = {
  presets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  value: VALUE_SHAPE.isRequired,
  onPreset: PropTypes.func.isRequired,
  chips: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }))
    .isRequired,
  onRemove: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default ExportFilters;
